// IEC 60909 模型细化：三相短路电流、峰值电流、最大/最小工况
// 说明：此实现为工程近似版，基于 IEC 60909 思路，便于页面联动；正式工程需完整序分、接地方式与最小/最大工况细化。

function seriesZ(a, b) { return { R: (a.R || 0) + (b.R || 0), X: (a.X || 0) + (b.X || 0) }; }
function parallelZ(a, b) {
  const denom = (a.R || 0 + b.R || 0) ** 2 + (a.X || 0 + b.X || 0) ** 2; // 非严格；近似并联：用导纳法更精确
  const Ya = { G: (a.R || 0) / ((a.R || 0) ** 2 + (a.X || 0) ** 2 || 1e-12), B: -(a.X || 0) / ((a.R || 0) ** 2 + (a.X || 0) ** 2 || 1e-12) };
  const Yb = { G: (b.R || 0) / ((b.R || 0) ** 2 + (b.X || 0) ** 2 || 1e-12), B: -(b.X || 0) / ((b.R || 0) ** 2 + (b.X || 0) ** 2 || 1e-12) };
  const Y = { G: Ya.G + Yb.G, B: Ya.B + Yb.B };
  const denomY = (Y.G ** 2 + Y.B ** 2) || 1e-12;
  return { R: Y.G / denomY, X: -Y.B / denomY };
}
function magZ(z) { return Math.sqrt((z.R || 0) ** 2 + (z.X || 0) ** 2); }
function XRtoZX(R_over_X, Zmag) { const k = R_over_X ? 1 / R_over_X : Infinity; const R = Zmag / Math.sqrt(1 + (k * k || 0)); const X = k * R; return { R, X }; }

function tempCorrectR(R20, tempC) { // IEC 60909 推荐：R(T) = R20 * (235 + T) / (235 + 20)
  const T = typeof tempC === 'number' ? tempC : 20;
  return (R20 || 0) * (235 + T) / 255;
}

function sourceFromShortCircuitPower({ Un_kV, S_sc_MVA, X_R = 10 }) {
  const Vll = (Un_kV || 0) * 1000;
  if (!Vll || !S_sc_MVA) return { R: 0, X: 0 };
  const Zmag = (Vll * Vll) / (S_sc_MVA * 1e6);
  const k = X_R; // X/R 比
  const R = Zmag / Math.sqrt(1 + k * k);
  const X = k * R;
  return { R, X };
}

function toOhmsTransformer({ Un_kV, S_tr_MVA, Z_percent }) {
  const V = (Un_kV || 0) * 1000; const S = (S_tr_MVA || 0) * 1e6; if (!V || !S || !Z_percent) return { R: 0, X: 0 };
  const Zbase = (V * V) / S; const Zmag = (Z_percent / 100) * Zbase; return { R: 0, X: Zmag };
}
function toOhmsLine({ R_per_km, X_per_km, length_km }) { return { R: (R_per_km || 0) * (length_km || 0), X: (X_per_km || 0) * (length_km || 0) }; }

function kappaPeakFactor(R_over_X) { // IEC 60909 峰值系数 k ≈ 1.02 + 0.98 * e^(-3 R/X)
  const rx = Math.max(0.0001, R_over_X || 0.0001);
  return 1.02 + 0.98 * Math.exp(-3 * rx);
}

export function calculateShortCircuitIEC60909Multi({
  Un_kV,
  source = {},
  transformer = {},
  line = {},
  temp_operate_c = 80,
  c_max = 1.1,
  c_min = 0.95,
  motors_percent = 0,
  motors_X_R = 3,
  ground_R_ohm = 0.5,
  ground_X_ohm = 0,
  z0_factor = 3
}) {
  const Vll = (Un_kV || 0) * 1000;
  const Vph = Vll / Math.sqrt(3);
  const Zs = sourceFromShortCircuitPower({ Un_kV, S_sc_MVA: source.S_sc_MVA, X_R: source.X_R || 10 });
  const Ztr = toOhmsTransformer({ Un_kV, S_tr_MVA: transformer.S_tr_MVA, Z_percent: transformer.uk_percent });
  const Zline20 = toOhmsLine({ R_per_km: line.R_per_km_ohm, X_per_km_ohm: line.X_per_km_ohm, length_km: line.length_km });
  const ZlineHot = { R: tempCorrectR(Zline20.R, temp_operate_c), X: Zline20.X };

  // 三相故障（复用上一函数的思路）
  let Znet_min = seriesZ(seriesZ(Zs, Ztr), Zline20);
  let Znet_maxR = seriesZ(seriesZ(Zs, Ztr), ZlineHot);
  if (motors_percent > 0 && source.S_sc_MVA) {
    const S_motor = Math.max(0, motors_percent) / 100 * source.S_sc_MVA;
    const Zm_mag = (Vll * Vll) / (S_motor * 1e6 || 1e-9);
    const k = motors_X_R || 3; const Rm = Zm_mag / Math.sqrt(1 + k * k); const Xm = k * Rm;
    Znet_min = parallelZ(Znet_min, { R: Rm, X: Xm });
  }
  const Ik3p_max = Vll && magZ(Znet_min) ? (c_max * Vll) / (Math.sqrt(3) * magZ(Znet_min)) : 0;
  const Ik3p_min = Vll && magZ(Znet_maxR) ? (c_min * Vll) / (Math.sqrt(3) * magZ(Znet_maxR)) : 0;
  const R_over_X_max = (Znet_min.R || 0) / (Znet_min.X || 1e-9);
  const k_peak = kappaPeakFactor(R_over_X_max);
  const Ip_max = Math.sqrt(2) * k_peak * Ik3p_max;

  // 两相短路（不接地）：I_LL = Vph / |Z1 + Z2|，其中 Vph = Vll/√3
  const Z1_min = seriesZ(seriesZ(Zs, Ztr), Zline20);
  const Z2_min = Z1_min; // 近似
  const Zsum_LL = { R: (Z1_min.R + Z2_min.R), X: (Z1_min.X + Z2_min.X) };
  const Ik_ll_max = Vph && magZ(Zsum_LL) ? (c_max * Vph) / magZ(Zsum_LL) : 0;

  // 零序路径与接地方式
  const vg = (transformer.vector_group || '').toUpperCase();
  const lowSideChar = vg.length >= 2 ? vg[1] : null; // 第二字符：低压侧 Y 或 D
  const hasNeutral = vg.includes('N');
  const neutralGround = (transformer.secondary_neutral_ground || '').toLowerCase();
  const zeroBlocked = (lowSideChar === 'D') || (lowSideChar === 'Y' && !hasNeutral) || neutralGround === 'none';
  const Z0_line = { R: (z0_factor || 3) * Zline20.R, X: (z0_factor || 3) * Zline20.X };
  const Z0_tr = zeroBlocked ? { R: 1e9, X: 1e9 } : Ztr; // 零序经变压器是否阻断
  const Zg3 = { R: 3 * (ground_R_ohm || 0), X: 3 * (ground_X_ohm || 0) }; // 3×Zf

  // 单相接地故障：I_1φG = Vph / |Z1 + Z2 + Z0 + 3Zf|
  const Zsum_1phg = { R: Z1_min.R + Z2_min.R + Z0_line.R + Z0_tr.R + Zg3.R, X: Z1_min.X + Z2_min.X + Z0_line.X + Z0_tr.X + Zg3.X };
  const Ik_1phg_max = Vph && magZ(Zsum_1phg) ? (c_max * Vph) / magZ(Zsum_1phg) : 0;

  // 两相接地故障（L-L-G）：I_LLG = Vph / |Z1 + (Z2 // (Z0 + 3Zf))|
  const Z0_total = { R: Z0_line.R + Z0_tr.R + Zg3.R, X: Z0_line.X + Z0_tr.X + Zg3.X };
  const Zpar_LLG = parallelZ(Z2_min, Z0_total);
  const Zsum_LLG = { R: Z1_min.R + Zpar_LLG.R, X: Z1_min.X + Zpar_LLG.X };
  const Ik_llg_max = Vph && magZ(Zsum_LLG) ? (c_max * Vph) / magZ(Zsum_LLG) : 0;

  return {
    three_phase: {
      Ik3p_max_A: Ik3p_max,
      Ik3p_min_A: Ik3p_min,
      Ip_max_A: Ip_max,
      Z_th_max_ohm: Znet_min,
      Z_th_min_ohm: Znet_maxR
    },
    two_phase_ll: {
      Ik_ll_max_A: Ik_ll_max
    },
    two_phase_earth: {
      Ik_llg_max_A: Ik_llg_max,
      params: { zeroBlocked, vector_group: transformer.vector_group, neutralGround: transformer.secondary_neutral_ground }
    },
    one_phase_earth: {
      Ik_1phg_max_A: Ik_1phg_max,
      params: { ground_R_ohm, ground_X_ohm, z0_factor, zeroBlocked }
    }
  };
}

// 兼容旧API（简化版），保留导出以免破坏现有页面逻辑
export function calculateShortCircuitIEC({ Un_kV, S_tr_MVA, Z_percent, R_per_km, X_per_km, length_km, motor_percent = 0 }) {
  const V_ll = (Un_kV || 0) * 1000;
  const Ztr = toOhmsTransformer({ Un_kV, S_tr_MVA, Z_percent });
  const Zline = toOhmsLine({ R_per_km, X_per_km, length_km });
  const Zeq_max = seriesZ(Ztr, Zline);
  const Zeq_min = { R: tempCorrectR(Zline.R, 80), X: Ztr.X + Zline.X };
  const I0_max = V_ll && magZ(Zeq_max) ? V_ll / (Math.sqrt(3) * magZ(Zeq_max)) : 0;
  const I0_min = V_ll && magZ(Zeq_min) ? V_ll / (Math.sqrt(3) * magZ(Zeq_min)) : 0;
  const motorFactor = 1 + Math.max(0, motor_percent) / 100;
  return { I_A_max: I0_max * motorFactor, I_A_min: I0_min, Z_eq_max_ohm: Zeq_max, Z_eq_min_ohm: Zeq_min };
}